import {
  ABI,
  BALANCE_DATA,
  CHAIN,
  GAS_DATA,
  LOCK_DATA,
  UNIT,
  USDC_DATA,
  WALLET_ALLOWANCE_AMOUNT,
  WALLET_ALLOWANCE_THRESHOLD,
  WALLET_GAS_CHECK_THRESHOLD,
  WALLET_UPDATE_INTERVAL,
} from '@/constants/wallet.js'
import { action, computed, observable, reaction, runInAction, when } from 'mobx'
import {
  createPublicClient,
  createWalletClient,
  custom,
  formatUnits,
  getAddress,
  isAddress,
  isAddressEqual,
  parseEventLogs,
  parseUnits,
} from 'viem'
import Scheduler from '@/utils/Scheduler.js'
import { durationFromDates, formatMweiAsUsd, formatMweiAsUsdCompact } from '@/utils/format.js'
import { getToastIconComponent } from '@/components/ui/sonner.jsx'
import { mdiCartOutline } from '@mdi/js'

export class WalletStore {
  rootStore = null

  #reactions = []
  #scheduler = null
  #publicClient = null
  #walletClient = null

  @observable accessor isInitializing = false
  @observable accessor isRefreshing = false
  @observable accessor address = ''
  @observable accessor gas = { ...GAS_DATA }
  @observable accessor usdc = { ...USDC_DATA }
  @observable accessor lock = { ...LOCK_DATA }
  @observable accessor balance = { ...BALANCE_DATA } // total balance (usdc + lock)

  constructor(rootStore) {
    this.rootStore = rootStore
    this.rootStore.registerDeconstructor(this.#_deconstructor)

    this.#reactions.push(reaction(() => [this.usdc.amount, this.lock.amount], this.#updateBalance))
    this.#scheduler = new Scheduler(this.#refresh, WALLET_UPDATE_INTERVAL, false)
  }

  #_deconstructor = () => {
    this.#reactions.forEach(x => x())
    this.#reactions = []
    this.reset()
    this.#scheduler.deconstructor()
  }

  init = async provider => {
    console.log('[Wallet] Initializing...')
    runInAction(() => {
      this.isInitializing = true
    })

    const accounts = await provider.request({ method: 'eth_requestAccounts' })
    runInAction(() => {
      this.address = getAddress(accounts[0])
    })

    this.#publicClient = createPublicClient({
      account: accounts[0],
      batch: { multicall: true },
      chain: CHAIN,
      transport: custom(provider),
    })
    this.#walletClient = createWalletClient({
      account: accounts[0],
      chain: CHAIN,
      transport: custom(provider),
    })
  }

  @action
  reset = () => {
    console.log('[Wallet] Resetting...')
    this.#scheduler.stop()

    this.#publicClient = null
    this.#walletClient = null

    this.address = ''
    this.gas = { ...GAS_DATA }
    this.usdc = { ...USDC_DATA }
    this.lock = { ...LOCK_DATA }
  }

  get analytics() {
    return this.rootStore.analyticsStore
  }

  get api() {
    return this.rootStore.apiStore
  }

  get jitsi() {
    return this.rootStore.jitsiStore
  }

  get notifications() {
    return this.rootStore.notificationsStore
  }

  get router() {
    return this.rootStore.routerStore
  }

  get meetingLivestreamView() {
    return this.rootStore.views.meetingLivestreamView
  }

  @computed
  get isInitialized() {
    return Boolean(this.address) && !this.isInitializing
  }

  @computed
  get hasLockBalance() {
    return this.lock.amount !== 0n
  }

  signMessage = async message =>
    this.#walletClient.signMessage({ message }).catch(e => {
      console.error('[Wallet] Failed to sign message', e)

      return false
    })

  transfer = async (to, amount) => {
    if (!isAddress(to, { strict: false })) {
      console.error('[Wallet] Cannot transfer to an invalid address')

      return false
    }

    to = getAddress(to) // eslint-disable-line no-param-reassign

    if (isAddressEqual(to, this.address)) {
      console.error('[Wallet] Cannot transfer to ourselves')

      return null
    }

    if (isNaN(amount)) {
      console.error('[Wallet] Cannot transfer an invalid amount type')

      return null
    }

    const amountInUnits = parseUnits(amount.toString(), UNIT.MWEI)

    if (amountInUnits <= 0) {
      console.error('[Wallet] Cannot transfer an invalid amount')

      return null
    }

    if (amountInUnits > this.balance.amount) {
      console.error('[Wallet] Insufficient balance for transfer')

      return null
    }

    try {
      console.log('[Wallet] Transferring...')
      const hash = await this.#abiTransfer(getAddress(to), amountInUnits)
      const receipt = await this.#getReceipt(hash)

      // TODO: error message for code -32603, insufficient gas

      if (receipt.success) {
        console.log('[Wallet] Transfer successful')
        this.updateAll().then()
        this.analytics.send(amount, to)?.then()
      }

      return receipt
    } catch (e) {
      console.error('[Wallet] Failed to transfer', e)

      return null
    }
  }

  tip = async (user, amount, postId = null) => {
    if (user.isAuthUser) {
      console.error('[Wallet] Cannot tip ourselves')

      return false
    }

    if (isNaN(amount)) {
      console.error('[Wallet] Cannot tip an invalid amount type')

      return false
    }

    const amountInUnits = parseUnits(amount.toString(), UNIT.MWEI)

    if (amountInUnits <= 0) {
      console.error('[Wallet] Cannot tip an invalid amount')

      return false
    }

    if (amountInUnits > this.balance.amount) {
      console.error('[Wallet] Insufficient balance to tip')

      return false
    }

    try {
      const response = await this.api.tip(amount, user.address, postId)
      const success = response?.data?.status === 'ok'

      if (success)
        if (postId === null) this.analytics.sendPaymentSuccess()?.then()
        else this.analytics.sendTipSuccess()?.then()

      return success
    } catch (e) {
      console.error('[Wallet] Failed to tip', e)

      return false
    }
  }

  beginPurchase = async post => {
    if (!post || post.mustTopUp || this.jitsi.isInitialized) return false

    const isMeetingOrLivestream = post.isMeeting || post.isLivestream
    if (isMeetingOrLivestream && post.willStartInTheFuture && !post.user.isAuthUser) return false

    const isPurchase = !post.canAccess
    const isPurchaseButNotMeetingOrLivestream = isPurchase && !isMeetingOrLivestream
    const response = await this.api.purchaseOrAccessPost(post).catch(null)

    if (!response?.data || (response.data.status && response.data.status === 'KO')) {
      this.notifications.toastError(
        isPurchaseButNotMeetingOrLivestream ? 'Purchase failed!' : 'Failed to access post!'
      )

      return false
    }

    if (Boolean(response.data.balance) && response.data.balance !== 'OK') {
      this.notifications.toastError('Insufficient balance!')

      return false
    }

    if (Boolean(response.data.allowance) && response.data.allowance !== 'OK') {
      this.notifications.toastError('Insufficient allowance!', {
        description: 'Please contact support.',
      })

      return false
    }

    if (
      (post.isMeeting || post.isLivestream) &&
      !post.isFree &&
      !post.user.isAuthUser &&
      !response.data.hostConnected
    ) {
      this.notifications.toastInfo('Event has not started', {
        description: 'Host has not joined yet. Please try again later.',
      })

      return false
    }

    post.updatePurchased(response.data)

    const sessionData = {
      isPurchase,
      jwt: response.data.jwt,
      region: response.data.region,
      roomName: response.data.roomName ?? response.data.hash,
    }

    if (isMeetingOrLivestream) {
      this.meetingLivestreamView.expect(post, sessionData)
      this.router
        .navigate(post.isMeeting ? this.router.routes.meeting : this.router.routes.livestream, {
          ignoreBlocker: true,
          params: { id: post.id },
        })
        .then()

      return true
    }

    await this.jitsi.init(post, sessionData)
    await when(() => this.jitsi.isConnected || !this.jitsi.isInitialized)

    if (!this.jitsi.isConnected) {
      this.notifications.toastError(
        isPurchaseButNotMeetingOrLivestream ? 'Purchase failed!' : 'Failed to access post!'
      )
      await this.jitsi.reset()

      return false
    }

    if (isPurchase && post.isOneTimePayment) {
      this.updateAll().then()
      this.notifications.toastSuccess('Purchase successful!', {
        description: `You spent ${post.priceFormatted}.`,
        icon: getToastIconComponent(mdiCartOutline),
      })
    }

    return true
  }

  finishPurchase = async post => {
    if (!post || post.isImage || (post.isVideo && !post.isHourlyRate)) {
      await this.jitsi.reset()

      return
    }

    const secondsViewed = this.jitsi.hourlyRateSecondsElapsed
    await this.jitsi.leave()

    if (!post.isHourlyRate) return

    const amountSpent = post.pricePerSecond * secondsViewed

    if (amountSpent > 0n)
      setTimeout(() => {
        this.notifications.toastSuccess('Payment successful!', {
          description: `You spent ${formatMweiAsUsd(amountSpent)} for watching ${durationFromDates(0, Number(secondsViewed) * 1000)}. Your balance will update shortly.`,
          icon: getToastIconComponent(mdiCartOutline),
        })
      }, 500)

    setTimeout(() => this.updateAll().then(), 5000) // BE needs time to process payment
  }

  updateAll = async () => {
    console.log('[Wallet] Updating...')

    if (this.#scheduler.isExecutingHandler) await this.#checkGas()
    else {
      this.#scheduler.stop()
      await Promise.all([this.#checkGas(true), this.#updateLock()])
      await this.#scheduler.start(false)
    }

    if (this.isInitializing)
      runInAction(() => {
        this.isInitializing = false
      })

    console.log('[Wallet] Updated')
  }

  #refresh = async () => {
    console.log('[Wallet] Refreshing...')

    runInAction(() => {
      this.isRefreshing = true
    })

    await Promise.all([this.#updateUsdc(), this.#updateLock()])

    runInAction(() => {
      this.isRefreshing = false
    })

    console.log('[Wallet] Refreshed')
  }

  #updateUsdc = async () => {
    const [usdc, allowance] = await Promise.all([this.#abiBalanceOf(), this.#abiAllowance()])

    runInAction(() => {
      this.usdc.amount = usdc
      this.usdc.amountFormatted = formatMweiAsUsd(usdc)
      this.usdc.amountFormattedCompact = formatMweiAsUsdCompact(usdc)

      this.usdc.allowance = allowance
      this.usdc.allowanceFormatted = formatMweiAsUsd(allowance)
      this.usdc.allowanceFormattedCompact = formatMweiAsUsdCompact(allowance)
    })

    await this.#checkAllowance(false)
  }

  #updateLock = async () => {
    const response = await this.api.getLockBalance()

    if (!response) {
      console.error('[Wallet] Failed to update lock balance')

      return
    }

    runInAction(() => {
      this.lock.amount = parseUnits(response.data.lockBalance.toString(), UNIT.MWEI)
      this.lock.amountFormatted = formatMweiAsUsd(this.lock.amount)
      this.lock.amountFormattedCompact = formatMweiAsUsdCompact(this.lock.amount)
    })
  }

  @action
  #updateBalance = () => {
    const total = this.usdc.amount + this.lock.amount // lock is always 0 or < 0
    const trend = this.balance.amount === total ? 0 : total - this.balance.amount >= 0 ? 1 : -1

    this.balance.amount = total
    this.balance.amountFormatted = formatMweiAsUsd(total)
    this.balance.amountFormattedCompact = formatMweiAsUsdCompact(total)
    this.balance.amountMwei = formatUnits(total, UNIT.MWEI)
    this.balance.trend = trend
  }

  #checkGas = async (force = false) => {
    if (this.gas.amount > WALLET_GAS_CHECK_THRESHOLD && !force) {
      console.log('[Wallet] Gas is above threshold, skipping gas check')

      return
    }

    const shouldRestartScheduler = this.#scheduler.isRunning
    const shouldUpdateEverything = !this.#scheduler.isExecutingHandler

    if (shouldUpdateEverything) {
      this.#scheduler.stop()
      runInAction(() => {
        this.isRefreshing = true
      })
    }

    try {
      console.log('[Wallet] Checking gas...')
      const response = await this.api.checkGas()

      if (!response) {
        console.error('[Wallet] Failed to request more gas')

        return
      }

      runInAction(() => {
        this.gas.amount = parseUnits(response.data.gasBalance.toString(), UNIT.ETHER)
        this.gas.amountFormatted = formatUnits(this.gas.amount, UNIT.ETHER)

        if (shouldUpdateEverything) {
          this.usdc.amount = parseUnits(response.data.usdc1.toString(), UNIT.MWEI)
          this.usdc.amountFormatted = formatMweiAsUsd(this.usdc.amount)
          this.usdc.amountFormattedCompact = formatMweiAsUsdCompact(this.usdc.amount)

          this.usdc.allowance = parseUnits(response.data.allowance1.toString(), UNIT.MWEI)
          this.usdc.allowanceFormatted = formatMweiAsUsd(this.usdc.allowance)
          this.usdc.allowanceFormattedCompact = formatMweiAsUsdCompact(this.usdc.allowance)
        }
      })

      if (shouldUpdateEverything) await this.#checkAllowance(true)
    } catch (e) {
      console.error('[Wallet] Failed to check gas', e)
    } finally {
      if (shouldRestartScheduler) this.#scheduler.start(false).then()
      if (shouldUpdateEverything)
        runInAction(() => {
          this.isRefreshing = false
        })
    }
  }

  #checkAllowance = async skipGasCheck => {
    if (this.usdc.allowance > WALLET_ALLOWANCE_THRESHOLD || this.usdc.amount <= 0) return

    try {
      console.log('[Wallet] Approving allowance...')
      const hash = await this.#abiApprove(skipGasCheck)
      const receipt = await this.#getReceipt(hash)

      if (receipt.error) return

      runInAction(() => {
        this.usdc.allowance = WALLET_ALLOWANCE_AMOUNT
        this.usdc.allowanceFormatted = formatMweiAsUsd(WALLET_ALLOWANCE_AMOUNT)
        this.usdc.allowanceFormattedCompact = formatMweiAsUsdCompact(WALLET_ALLOWANCE_AMOUNT)
      })

      console.log('[Wallet] Allowance approved')
    } catch (e) {
      console.error('[Wallet] Failed to approve allowance', e)
    }
  }

  #simulateContract = async (functionName, args, params = {}) => {
    const response = await this.#publicClient.simulateContract({
      abi: ABI,
      address: USDC_DATA.address,
      args,
      functionName,
      ...params,
    })

    if (!response.result) console.error('[Wallet] Failed to simulate contract', response)

    return response
  }

  #getReceipt = async hash => {
    try {
      const receipt = await this.#publicClient.waitForTransactionReceipt({ hash })
      const success = receipt.status === 'success'
      let parsedLogs = []

      if (!success)
        try {
          parsedLogs = parseEventLogs({ abi: ABI, logs: receipt.logs })
        } catch (e) {
          console.error('[Wallet] Failed to parse logs', e)
        }

      return {
        ...receipt,
        error: !success,
        parsedLogs,
        success,
      }
    } catch (e) {
      console.error('[Wallet] Failed to get receipt', e)

      return {
        error: true,
        parsedLogs: [],
        success: false,
      }
    }
  }

  #abiAllowance = async () =>
    this.#publicClient.readContract({
      abi: ABI,
      address: USDC_DATA.address,
      args: [this.address, import.meta.env.VITE_PROMEET_SETTLEMENT_ADDRESS],
      functionName: 'allowance',
    })

  #abiApprove = async skipGasCheck => {
    if (!skipGasCheck) await this.#checkGas()

    const response = await this.#simulateContract('approve', [
      import.meta.env.VITE_PROMEET_SETTLEMENT_ADDRESS,
      WALLET_ALLOWANCE_AMOUNT,
    ])

    return response.result ? this.#walletClient.writeContract(response.request) : ''
  }

  #abiBalanceOf = async () =>
    this.#publicClient.readContract({
      abi: ABI,
      address: USDC_DATA.address,
      args: [this.address],
      functionName: 'balanceOf',
    })

  #abiTransfer = async (to, amount) => {
    await this.#checkGas()

    const nonce = await this.#publicClient.getTransactionCount({
      address: this.address,
      blockTag: 'pending',
    })
    const response = await this.#simulateContract('transfer', [to, amount], { nonce })

    return response.result ? this.#walletClient.writeContract(response.request) : ''
  }
}
